import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Trash2, Loader2 } from 'lucide-react';
import { apiFetch } from '../../../services/http';
import type { Campaign } from '../campaignsApi';
import './delete-campaign-draft.css';

interface Props {
  campaign: Campaign;
  init?: RequestInit;
  onDeleted: () => void;
}

const draftStatuses = ['draft', 'rascunho', 'briefing', 'backlog', 'delete_failed', 'deleting'];

export function DeleteCampaignDraftButton({ campaign, init, onDeleted }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const deletion = useMutation({
    mutationFn: () => apiFetch(`/v1/crm/campaigns/${campaign.id}`, { ...init, method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.removeQueries({ queryKey: ['campaign', campaign.id] });
      setIsOpen(false);
      onDeleted();
    },
  });
  if (!draftStatuses.includes(campaign.status)) return null;
  const origins = [campaign.meta_campaign_id || campaign.meta_adset_id || campaign.meta_ad_id ? 'Meta' : null,
    campaign.google_campaign_id || campaign.google_ad_id ? 'Google Ads' : null].filter(Boolean);

  function openConfirmation() {
    deletion.reset();
    setIsOpen(true);
  }

  return <>
    <button type="button" className="draft-delete-trigger" onClick={openConfirmation}>
      <Trash2 size={16} /> Excluir rascunho
    </button>
    {isOpen && createPortal(
      <div className="draft-delete-backdrop" onKeyDown={(event) => {
        if (event.key === 'Escape' && !deletion.isPending) setIsOpen(false);
        if (event.key === 'Tab') {
          const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
          }
        }
      }}>
        <section role="alertdialog" aria-modal="true" aria-labelledby={`delete-draft-${campaign.id}`}
          aria-describedby={`delete-description-${campaign.id}`} className="draft-delete-dialog">
          <h2 id={`delete-draft-${campaign.id}`}>Excluir rascunho?</h2>
          <p id={`delete-description-${campaign.id}`}>
            <strong>{campaign.name}</strong> será excluído do CRM
            {origins.length ? ` e removido de ${origins.join(' e ')}` : ''}.
            {origins.length ? ' A remoção na origem não pode ser desfeita por esta tela.' : ''}
          </p>
          <p>Se a origem não confirmar a remoção, o rascunho permanecerá para nova tentativa.</p>
          {deletion.isError && <p role="alert" className="draft-delete-error">
            {deletion.error instanceof Error ? deletion.error.message : 'Não foi possível excluir o rascunho.'}
          </p>}
          <div className="draft-delete-actions">
            <button type="button" autoFocus disabled={deletion.isPending} onClick={() => setIsOpen(false)}>Cancelar</button>
            <button type="button" className="draft-delete-confirm" disabled={deletion.isPending}
              onClick={() => deletion.mutate()}>
              {deletion.isPending && <Loader2 size={16} className="spin" />}
              {deletion.isPending ? 'Excluindo...' : 'Confirmar exclusão'}
            </button>
          </div>
        </section>
      </div>, document.body)}
  </>;
}
